import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import {
  fetchTableFilterData,
  TableFilterQueryKey,
} from '../../api/portalGateway/tableFilterData';
import { useStore } from '../../store';
import {
  useFilterSelections,
  useFilterSelectionsForQuery,
} from '../../store/hooks/useFilterSelections';
import { FilterConfigDto } from '../../store/states/filterTypes';
import { extractErrorMessage } from '../../utils/errorMessage';
import { prependRoleId } from '../../utils/prependRoleId';

import { FiltersComponent } from './FiltersComponent';
import { validateFilters } from './validateFilters';

export type TableFilterProps = {
  config?: { filters?: FilterConfigDto[] };
  pageFilterInstanceId: string;
  widgetId: string;
  widgetFilterInstanceId: string;
};

const hasFilters = (
  config: TableFilterProps['config']
): config is Exclude<TableFilterProps['config'], undefined> =>
  Boolean(config?.filters?.length);

export const TableFilters = ({
  config,
  pageFilterInstanceId,
  widgetId,
  widgetFilterInstanceId,
}: TableFilterProps) => {
  const { t } = useTranslation();

  const pageFilterSelections = useFilterSelections(pageFilterInstanceId);
  const tableFilterSelections = useFilterSelectionsForQuery(
    widgetFilterInstanceId
  );

  const [
    isTableFilterDataLoaded,
    updateFilterData,
    setFilterError,
    resetFilterSelection,
  ] = useStore(
    useShallow((state) => [
      state.filterData[widgetId]?.meta.loaded,
      state.updateFilterData,
      state.setFilterError,
      state.resetFilterSelection,
    ])
  );

  const { data, error, isFetching } = useQuery({
    queryKey: prependRoleId<TableFilterQueryKey>([
      'tableFilters',
      widgetId,
      pageFilterSelections,
      tableFilterSelections,
    ]),
    queryFn: fetchTableFilterData,
    enabled: hasFilters(config),
  });

  useEffect(() => {
    if (data?.data != null && hasFilters(config)) {
      const filters = validateFilters(config, data);
      // set data for all filters after validation
      updateFilterData(widgetFilterInstanceId, filters);
    } else if (hasFilters(config) && error != null) {
      setFilterError(widgetFilterInstanceId, extractErrorMessage(error));
    }
  }, [
    config,
    widgetFilterInstanceId,
    data,
    error,
    updateFilterData,
    setFilterError,
    // Added as a dependency so that data is set on the store after a filter reset, no matter if it's changed or not
    isTableFilterDataLoaded,
  ]);

  // only show filters if at least one is configured
  if (!hasFilters(config)) {
    return undefined;
  }

  return (
    <FiltersComponent
      config={config}
      entityId={widgetFilterInstanceId}
      error={error}
      isFetching={isFetching}
      resetFilterSelection={resetFilterSelection}
      translate={t}
      embedded={true}
    />
  );
};
